/** Client-only Insights UX constants (no env reads). */

export const INSIGHTS_ASK_TIMEOUT_MS = 120_000;

export const INSIGHTS_RE_PRESENT_CACHE_MISS_MESSAGE =
  'Previous results are no longer in memory — run the original question again.';

export const INSIGHTS_ASK_TIMEOUT_MESSAGE =
  'Request timed out — try a narrower question or click Cancel.';

/** Ordered SSE step keys for the agent activity timeline. */
export const INSIGHTS_SSE_STEP_ORDER = Object.freeze([
  { key: 'preparing', label: 'Preparing' },
  { key: 'parsing', label: 'Understanding your question' },
  { key: 'routing', label: 'Choosing the best approach' },
  { key: 'planning', label: 'Planning the query' },
  { key: 'resolving_entity', label: 'Resolving entities' },
  { key: 'translating', label: 'Translating to database query' },
  { key: 'querying', label: 'Querying your data' },
  { key: 'observing', label: 'Reviewing results' },
  { key: 'replanning', label: 'Refining the query' },
  { key: 'formatting', label: 'Formatting results' },
  { key: 'summarizing', label: 'Summarizing' },
]);

/** Non-success outcomes rendered as advisor messages, not system errors. */
export const INSIGHTS_SOFT_OUTCOMES = Object.freeze([
  'no_data',
  'cannot_answer',
  'low_confidence',
  'unsupported',
]);

export const INSIGHTS_AUDIT_WRITE_FAILED_NOTICE =
  'Your result is shown below, but activity logging may be incomplete.';

export const INSIGHTS_EXAMPLE_GROUPS = Object.freeze([
  {
    label: 'Activity',
    icon: 'pi-users',
    questions: [
      'Show learners who have never logged in',
      'Show learners inactive recently',
    ],
  },
  {
    label: 'Courses',
    icon: 'pi-book',
    questions: [
      'Show learners who completed any course recently',
      'Which learners have performance below 70% in any course?',
    ],
  },
  {
    label: 'Payments',
    icon: 'pi-wallet',
    questions: [
      'Show learners with overdue payments',
    ],
  },
]);

export function flattenExampleStarterCards(groups = INSIGHTS_EXAMPLE_GROUPS) {
  const cards = [];
  for (const group of groups) {
    for (const question of group.questions) {
      cards.push({
        label: group.label,
        icon: group.icon || 'pi-chart-line',
        question,
      });
    }
  }
  return cards;
}

export function createTurnId() {
  return `turn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function buildInitialAgentSteps() {
  return INSIGHTS_SSE_STEP_ORDER.map((step) => ({
    key: step.key,
    label: step.label,
    status: 'pending',
    message: null,
  }));
}

export function applySseStepToAgentSteps(steps, stepPayload) {
  const key = stepPayload?.id || stepPayload?.key || stepPayload?.step;
  const message = stepPayload?.message || null;
  if (!key) return steps;

  const order = INSIGHTS_SSE_STEP_ORDER.map((s) => s.key);
  const activeIndex = order.indexOf(key);
  if (activeIndex < 0) {
    return steps.map((s) => (
      s.key === key
        ? { ...s, status: 'active', message: message || s.message }
        : s
    ));
  }

  return steps.map((s) => {
    const idx = order.indexOf(s.key);
    if (idx < 0) return s;
    if (idx < activeIndex) {
      return { ...s, status: 'done', message: s.message };
    }
    if (idx === activeIndex) {
      return { ...s, status: 'active', message: message || s.label };
    }
    return { ...s, status: 'pending', message: null };
  });
}

export function finalizeAgentSteps(steps) {
  return steps.map((s) => (
    s.status === 'pending' || s.status === 'active'
      ? { ...s, status: 'done' }
      : s
  ));
}

export function buildScopeChipLabels(scope, answerMeta) {
  const chips = [];
  const meta = answerMeta || {};
  const sc = scope || {};

  if (meta.population) {
    chips.push(meta.population);
  } else if (sc.activity === 'login') {
    chips.push('Login activity');
  } else if (sc.activity === 'course') {
    chips.push('Course activity');
  }

  if (meta.activity && !chips.includes(meta.activity)) {
    chips.push(meta.activity);
  }

  if (meta.timeWindow) {
    chips.push(meta.timeWindow);
  } else if (sc.timeWindowDays) {
    chips.push(`Last ${sc.timeWindowDays} days`);
  }

  if (sc.courseLabel) {
    chips.push(sc.courseLabel);
  }

  if (meta.subjectRoleFilter) {
    chips.push(meta.subjectRoleFilter);
  }

  return chips;
}

export function isSoftAdvisorOutcome(outcome) {
  return INSIGHTS_SOFT_OUTCOMES.includes(outcome);
}

export const INSIGHTS_RECENT_QUESTIONS_MAX = 5;

export const INSIGHTS_RECENT_QUESTIONS_DEVICE_NOTE =
  'Saved on this device for your account.';

/**
 * @param {string} userId
 * @param {string} institutionId
 * @returns {string|null}
 */
export function buildRecentQuestionsScopeKey(userId, institutionId) {
  const uid = String(userId || '').trim();
  const iid = String(institutionId || '').trim();
  if (!uid || !iid) return null;
  return `${uid}|${iid}`;
}

/**
 * @param {string} question
 * @returns {string}
 */
export function normalizeRecentQuestionText(question) {
  return String(question || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * @param {{ askedAt?: number|string|Date }} item
 * @returns {string}
 */
export function formatRecentQuestionAskedAt({ askedAt } = {}) {
  const ts = askedAt != null ? new Date(askedAt).getTime() : NaN;
  if (!Number.isFinite(ts)) return '';
  return formatRelativeTime(ts);
}

/**
 * @param {number} timestampMs
 * @returns {string}
 */
function formatRelativeTime(timestampMs) {
  const diffMs = Date.now() - timestampMs;
  if (diffMs < 0) return 'Just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(timestampMs).toLocaleDateString();
}

/**
 * @param {Array<{ question: string, askedAt: number }>} list
 * @param {{ question: string, askedAt: number }} entry
 * @param {number} [max]
 * @returns {Array<{ question: string, askedAt: number }>}
 */
export function upsertRecentQuestion(list, entry, max = INSIGHTS_RECENT_QUESTIONS_MAX) {
  const question = String(entry?.question || '').trim();
  if (!question) return Array.isArray(list) ? [...list] : [];

  const key = normalizeRecentQuestionText(question);
  const prior = Array.isArray(list) ? list : [];
  const filtered = prior.filter(
    (item) => normalizeRecentQuestionText(item?.question) !== key,
  );
  return [
    {
      question,
      askedAt: entry.askedAt ?? Date.now(),
    },
    ...filtered,
  ].slice(0, max);
}
